import React, { useState, useRef, useEffect, useCallback } from 'react';
import ProductCard from './ProductCard';
import { Copy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getProducts, getCategories, getFeaturedProducts, getBanners, getActiveCoupons, getHomepageSections, API_URL, apiPath } from '../api';
import { usePricingRefresh } from '../hooks/usePricingRefresh';
import { Product } from '../types';
import { formatINR } from '../utils/currency';
import { getImageUrl } from '../utils/imageUtils';
import FullScreenBannerSlider from './FullScreenBannerSlider';
import HomepageProductSections, {
  DynamicHomepageSection,
  isProductHomepageSection,
} from './HomepageProductSections';

interface Category {
  _id: string;
  name: string;
  slug: string;
  image?: string;
}

interface Brand {
  _id: string;
  name: string;
  slug: string;
  logo?: string;
}

const Homepage = () => {
  const pricingRefresh = usePricingRefresh();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [banners, setBanners] = useState<any[]>([]);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [homepageSections, setHomepageSections] = useState<DynamicHomepageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const hasAdminProductSections = homepageSections.some(
    (s) => isProductHomepageSection(s) && (s.products?.length ?? 0) > 0
  );

  

  // Fetch data on component mount
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching homepage data...');
      
      // Fetch all data with individual error handling
      let cats: Category[] = [];
      let prods: Product[] = [];
      let featured: Product[] = [];
      let bannerData: any[] = [];
      let couponData: any[] = [];
      let sectionsData: any[] = [];

      // Fetch categories
      try {
        const categoriesRes = await getCategories();
        console.log('Categories response:', categoriesRes);
        if (categoriesRes && categoriesRes.success && Array.isArray(categoriesRes.data)) {
          cats = categoriesRes.data;
        } else if (Array.isArray(categoriesRes)) {
          cats = categoriesRes;
        } else {
          console.warn('Categories response format unexpected:', categoriesRes);
          cats = [];
        }
      } catch (e) { console.error('Categories fetch failed:', e); }

      // Fetch products
      try {
        const productsRes = await getProducts();
        prods = Array.isArray(productsRes.data) ? productsRes.data : (productsRes.data?.products || []);
      } catch (e) { console.error('Products fetch failed:', e); }

      // Fetch featured products
      try {
        const featuredRes = await getFeaturedProducts();
        featured = Array.isArray(featuredRes.data) ? featuredRes.data : (featuredRes.data?.products || []);
      } catch (e) { console.error('Featured products fetch failed:', e); }

      // Fetch banners
      try {
        const bannersRes = await getBanners();
        bannerData = Array.isArray(bannersRes.data) ? bannersRes.data : [];
        console.log('Banners received:', bannerData.length, bannerData);
      } catch (e) { console.error('Banners fetch failed:', e); }

      // Fetch coupons
      try {
        const couponsRes = await getActiveCoupons();
        couponData = Array.isArray(couponsRes.data) ? couponsRes.data : [];
      } catch (e) { console.error('Coupons fetch failed:', e); }

      // Fetch brands
      try {
        const brandsRes = await fetch(`${API_URL}/brands/public`).then(res => res.json());
        if (brandsRes.success) {
          setBrands(brandsRes.data);
        }
      } catch (e) { console.error('Brands fetch failed:', e); }
      try {
        const sectionsRes = await getHomepageSections();
        const rawSections = sectionsRes.success ? sectionsRes.data : [];
        sectionsData = rawSections.map((s: any) => ({
          ...s,
          products: s.products || s.config?.products || [],
        }));
      } catch (e) { console.error('Homepage sections fetch failed:', e); }
      
      console.log('Parsed data:', { cats: cats.length, prods: prods.length, featured: featured.length, banners: bannerData.length, coupons: couponData.length });
      
      setCategories(cats);
      setProducts(prods);
      setFeaturedProducts(featured);
      setBanners(bannerData);
      setCoupons(couponData);
      setHomepageSections(sectionsData);
      setError(null);
      setRetryCount(0);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      const errorMessage = err?.message || 'Failed to load data. Please try again.';
      setError(errorMessage);
      
      // Auto-retry after 2 seconds if it's a network error
      if (retryCount < 2 && (err?.message?.includes('timeout') || err?.message?.includes('fetch'))) {
        console.log(`Auto-retrying in 2 seconds (attempt ${retryCount + 1}/2)...`);
        setTimeout(() => {
          setRetryCount(retryCount + 1);
          fetchData();
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData, pricingRefresh]);

  const handleCopyCoupon = () => {
    const couponCode = coupons.length > 0 ? coupons[0].code : 'WELCOME10';
    navigator.clipboard.writeText(couponCode);
    setCopiedCoupon(true);
    setTimeout(() => setCopiedCoupon(false), 2000);
  };


  const ProductGrid = ({ title, subtitle, products }: { title: string; subtitle?: string; products: Product[] }) => (
    <section className="py-8 md:py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mb-2">{title}</h2>
          {subtitle && <p className="text-gray-600 text-sm md:text-base">{subtitle}</p>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {(products || []).slice(0, 8).map((product) => (
            <ProductCard key={product._id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchData}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full pb-16 md:pb-0 flex flex-col">
      {/* Full-Screen Banner Slider */}
      {banners.length > 0 && (
        <FullScreenBannerSlider 
          banners={banners.filter(b => b.position === 'hero' || !b.position).map(b => {
            return {
              _id: b._id,
              image: b.image || '',
              order: b.order || 0,
              isActive: b.isActive !== false 
            };
          })} 
          autoPlayInterval={5000} 
        />
      )}

      {/* Admin-Controlled Coupon Strip */}
      {coupons.length > 0 && (
        <section className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 sm:py-3">
          <div className="container mx-auto px-2 sm:px-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
              <div className="text-center sm:text-left">
                <h3 className="text-xs sm:text-sm font-bold">EXCLUSIVE OFFER</h3>
                <p className="text-xs sm:text-sm">
                  Use code <span className="font-bold text-yellow-300">{coupons[0].code}</span> for {coupons[0].discountPercentage}% off
                  {coupons[0].minPurchaseAmount > 0 && ` on orders over ${formatINR(coupons[0].minPurchaseAmount)}`}
                </p>
              </div>
              <button
                onClick={handleCopyCoupon}
                className="bg-white text-orange-600 px-3 py-1 rounded font-semibold hover:bg-gray-100 transition-colors flex items-center gap-1 text-xs sm:text-sm whitespace-nowrap"
                aria-label="Copy coupon code"
              >
                <Copy className="w-3 h-3" />
                {copiedCoupon ? 'Copied!' : 'Copy'}
              </button>
              {copiedCoupon && (
                <span className="text-green-300 text-xs">Copied!</span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Products Showcase - fallback when no admin-managed sections */}
      {products.length > 0 && !hasAdminProductSections && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Featured Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {(products || []).slice(0, 8).map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/products"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                View All Products
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Admin-managed product sections — upper zone only */}
      <div className="home-dynamic-sections" data-home-zone="dynamic">
        <HomepageProductSections sections={homepageSections} />
        {featuredProducts.length > 0 && !hasAdminProductSections && (
          <ProductGrid title="Featured Products" products={featuredProducts} />
        )}
      </div>

      {/* Fixed bottom: Shop by Category + Shop by Brand (above footer newsletter) */}
      <div className="home-bottom-anchored mt-auto w-full" data-home-zone="bottom-anchored">
      {categories.length > 0 && (
        <section id="home-shop-by-category" aria-label="Shop by Category" className="py-8 md:py-12 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800">Shop by Category</h2>
              <Link
                to="/categories"
                className="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
              >
                View All →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
              {categories.slice(0, 8).map((category) => (
                <Link
                  key={category._id}
                  to={`/category/${category.slug}`}
                  className="bg-gray-100 rounded-lg p-3 md:p-4 text-center hover:bg-gray-200 transition-colors"
                >
                  {category.image && (
                    <img
                      src={getImageUrl(category.image)}
                      alt={category.name}
                      className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-2 object-cover rounded-lg"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <div className="font-semibold text-gray-800 text-sm md:text-base">{category.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {brands.length > 0 && (
        <section
          id="home-shop-by-brand"
          aria-label="Shop by Brand"
          className="py-8 md:py-12 bg-gray-50 border-t border-gray-100"
        >
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800">Shop by Brand</h2>
              <Link
                to="/brands"
                className="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
              >
                View All →
              </Link>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 md:gap-6">
              {(brands || []).slice(0, 20).map((brand) => (
                <Link
                  key={brand._id}
                  to={`/brands/${brand.slug}`}
                  className="flex flex-col items-center text-center group"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white border border-gray-200 flex items-center justify-center mb-2 md:mb-3 group-hover:shadow-lg transition-shadow overflow-hidden">
                    {brand.logo ? (
                      <img
                        src={getImageUrl(brand.logo)}
                        alt={brand.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-xl md:text-2xl font-bold text-gray-400">
                        {brand.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="text-xs md:text-sm font-medium text-gray-800 group-hover:text-blue-600 transition-colors">
                    {brand.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      </div>

    </div>
  );
};

export default Homepage;
